
import { useState } from "react";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { UserRole } from "@/types";
import { useAuth } from "@/contexts/AuthContext";
import { AlertCircle } from "lucide-react";

// Schema para validação
const userSchema = z.object({
  nome: z.string().min(3, "O nome deve ter pelo menos 3 caracteres"),
  email: z.string().email("E-mail inválido"),
  role: z.nativeEnum(UserRole, {
    errorMap: () => ({ message: "Selecione um perfil válido" }),
  }),
  senha: z
    .string()
    .min(6, "A senha deve ter pelo menos 6 caracteres")
    .optional(),
  confirmacaoSenha: z.string().optional(),
  permissions: z.object({
    canManageUsers: z.boolean().optional(),
    canManagePartners: z.boolean().optional(),
    canManageClients: z.boolean().optional(),
    canManageGenerators: z.boolean().optional(),
    canViewReports: z.boolean().optional(),
    canAccessCalculator: z.boolean().optional(),
    canManageSolarProjects: z.boolean().optional(),
    canManageEnergyTrading: z.boolean().optional(),
    canViewFinancials: z.boolean().optional(),
    canEditFinancials: z.boolean().optional(),
    canExportData: z.boolean().optional(),
    canImportData: z.boolean().optional(),
    canManageWorkflows: z.boolean().optional(),
  }).optional(),
}).refine(data => {
  // Se for cadastro (com senha) verifica se as senhas coincidem
  if (data.senha) {
    return data.senha === data.confirmacaoSenha;
  }
  return true;
}, {
  message: "As senhas não coincidem",
  path: ["confirmacaoSenha"],
});

interface UserFormProps {
  user?: {
    id: string;
    nome: string;
    email: string;
    role: UserRole;
    permissions?: {
      canManageUsers?: boolean;
      canManagePartners?: boolean;
      canManageClients?: boolean;
      canManageGenerators?: boolean;
      canViewReports?: boolean;
      canAccessCalculator?: boolean;
      canManageSolarProjects?: boolean;
      canManageEnergyTrading?: boolean;
      canViewFinancials?: boolean;
      canEditFinancials?: boolean;
      canExportData?: boolean;
      canImportData?: boolean;
      canManageWorkflows?: boolean;
    };
  };
  onSuccess: () => void;
  onCancel: () => void;
  readOnlyPermissions?: boolean;
}

// Lista de todas as permissões disponíveis no sistema
const permissionsList = [
  { id: "canManageUsers", label: "Gerenciar Usuários", description: "Permite criar, editar e excluir usuários do sistema" },
  { id: "canManagePartners", label: "Gerenciar Parceiros", description: "Permite gerenciar informações de parceiros" },
  { id: "canManageClients", label: "Gerenciar Clientes", description: "Permite criar, editar e excluir clientes" },
  { id: "canManageGenerators", label: "Gerenciar Geradores", description: "Permite criar, editar e excluir geradores de energia" },
  { id: "canViewReports", label: "Visualizar Relatórios", description: "Permite acessar todos os relatórios do sistema" },
  { id: "canAccessCalculator", label: "Acessar Calculadora", description: "Permite utilizar a calculadora solar" },
  { id: "canManageSolarProjects", label: "Gerenciar Projetos Solares", description: "Permite gerenciar o fluxo de projetos solares" },
  { id: "canManageEnergyTrading", label: "Gerenciar Comercialização", description: "Permite gerenciar a comercialização de energia" },
  { id: "canViewFinancials", label: "Visualizar Financeiro", description: "Permite visualizar dados financeiros" },
  { id: "canEditFinancials", label: "Editar Financeiro", description: "Permite editar dados financeiros" },
  { id: "canExportData", label: "Exportar Dados", description: "Permite exportar dados do sistema" },
  { id: "canImportData", label: "Importar Dados", description: "Permite importar dados para o sistema" },
  { id: "canManageWorkflows", label: "Gerenciar Fluxos", description: "Permite configurar fluxos de trabalho no sistema" },
] as const; // Make this const assertion to create a readonly array with literal types

// Type for permission IDs
type PermissionId = typeof permissionsList[number]['id'];

export const UserForm = ({ user, onSuccess, onCancel, readOnlyPermissions = false }: UserFormProps) => {
  const { register, updateUser } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isEditing = !!user;

  // Valores padrão do formulário
  const defaultValues = {
    nome: user?.nome || "",
    email: user?.email || "",
    role: user?.role || UserRole.VENDEDOR,
    senha: "",
    confirmacaoSenha: "",
    permissions: user?.permissions || {
      canManageUsers: false,
      canManagePartners: false,
      canManageClients: false,
      canManageGenerators: false,
      canViewReports: false,
      canAccessCalculator: false,
      canManageSolarProjects: false,
      canManageEnergyTrading: false,
      canViewFinancials: false,
      canEditFinancials: false,
      canExportData: false,
      canImportData: false,
      canManageWorkflows: false,
    }
  };

  // Configuração do formulário
  const form = useForm({
    resolver: zodResolver(userSchema),
    defaultValues,
  });

  // Função para aplicar permissões padrão com base no perfil
  const handleRoleChange = (role: UserRole) => {
    if (readOnlyPermissions) return;
    
    // Define permissões padrão com base no perfil selecionado
    let defaultPermissions: Record<PermissionId, boolean> = {} as Record<PermissionId, boolean>;
    
    if (role === UserRole.ADMIN) {
      // Administradores têm todas as permissões
      permissionsList.forEach(permission => {
        defaultPermissions[permission.id] = true;
      });
    } else if (role === UserRole.PARCEIRO) {
      // Parceiros têm permissões limitadas
      defaultPermissions = {
        canManageUsers: false,
        canManagePartners: false,
        canManageClients: true,
        canManageGenerators: false,
        canViewReports: true,
        canAccessCalculator: true,
        canManageSolarProjects: true,
        canManageEnergyTrading: false,
        canViewFinancials: true,
        canEditFinancials: false,
        canExportData: true,
        canImportData: false,
        canManageWorkflows: false,
      };
    } else {
      // Vendedores têm permissões básicas
      defaultPermissions = {
        canManageUsers: false,
        canManagePartners: false,
        canManageClients: true,
        canManageGenerators: false,
        canViewReports: true,
        canAccessCalculator: false,
        canManageSolarProjects: false,
        canManageEnergyTrading: false,
        canViewFinancials: false,
        canEditFinancials: false,
        canExportData: false,
        canImportData: false,
        canManageWorkflows: false,
      };
    }
    
    // Atualiza as permissões no formulário
    form.setValue('permissions', defaultPermissions);
  };

  // Função para salvar
  const onSubmit = async (values: z.infer<typeof userSchema>) => {
    setIsSubmitting(true);
    try {
      if (isEditing) {
        // Atualizar usuário existente
        const { senha, confirmacaoSenha, ...userData } = values;
        const success = await updateUser(user.id, userData);
        if (success) {
          onSuccess();
        }
      } else {
        // Criar novo usuário
        const success = await register(
          values.nome,
          values.email,
          values.senha || "", // Senha deve existir em modo de criação
          values.role,
          values.permissions
        );
        if (success) {
          onSuccess();
        }
      }
    } catch (error) {
      console.error("Erro ao salvar usuário:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 py-4">
        <FormField
          control={form.control}
          name="nome"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Nome</FormLabel>
              <FormControl>
                <Input placeholder="Nome do usuário" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>E-mail</FormLabel>
              <FormControl>
                <Input placeholder="email@exemplo.com" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="role"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Perfil</FormLabel>
              <Select
                onValueChange={(value) => {
                  field.onChange(value);
                  handleRoleChange(value as UserRole);
                }}
                defaultValue={field.value}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione um perfil" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value={UserRole.ADMIN}>Administrador</SelectItem>
                  <SelectItem value={UserRole.VENDEDOR}>Vendedor</SelectItem>
                  <SelectItem value={UserRole.PARCEIRO}>Parceiro</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        {!isEditing && (
          <>
            <FormField
              control={form.control}
              name="senha"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Senha</FormLabel>
                  <FormControl>
                    <Input type="password" placeholder="******" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="confirmacaoSenha"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Confirmar Senha</FormLabel>
                  <FormControl>
                    <Input
                      type="password"
                      placeholder="******"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </>
        )}

        <div className="mt-8">
          <h3 className="text-lg font-medium mb-2">Permissões do Usuário</h3>
          <Separator className="mb-4" />
          
          {readOnlyPermissions && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 mb-4 flex items-start gap-2">
              <AlertCircle className="text-yellow-500 h-5 w-5 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-yellow-800">Permissões disponíveis apenas para Administradores</p>
                <p className="text-sm text-yellow-700 mt-1">
                  Você está visualizando as permissões do usuário, mas apenas administradores podem modificá-las.
                </p>
              </div>
            </div>
          )}
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {permissionsList.map((permission) => (
              <FormField
                key={permission.id}
                control={form.control}
                name={`permissions.${permission.id}` as const}
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                    <FormControl>
                      <Checkbox
                        checked={field.value as boolean | undefined}
                        onCheckedChange={field.onChange}
                        disabled={readOnlyPermissions}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel className="font-medium">
                        {permission.label}
                      </FormLabel>
                      <FormDescription className="text-xs">
                        {permission.description}
                      </FormDescription>
                    </div>
                  </FormItem>
                )}
              />
            ))}
          </div>
        </div>

        <DialogFooter className="mt-6">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancelar
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Salvando..." : isEditing ? "Atualizar" : "Criar"}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
};
